import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { showAlert } from "@/lib/alert";
import type { InspectionAngleValue } from "@/lib/inspection-tier-requirements";
import {
  normalizeImageAsset,
  pickImageFromCamera,
  pickImagesFromLibrary,
  readLocalFileSize,
  recordVideoFromCamera,
  validateFileSize,
  type ImagePickerAsset,
} from "@/lib/image-picker";
import { MAX_UPLOAD_SIZE } from "@/lib/validation/file";
import Ionicons from "@react-native-vector-icons/ionicons";
import React, { useState } from "react";
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { BrandColors, Colors } from "../../constants/theme";
import { ThemedText } from "../themed-text";

export interface CapturedMedia {
  uri: string;
  type: "photo" | "video";
  id: string;
  angle?: InspectionAngleValue;
}

interface MediaCaptureProps {
  media: CapturedMedia[];
  onMediaAdded: (media: CapturedMedia) => void;
  onMediaRemoved: (id: string) => void;
  maxPhotos?: number;
  maxVideos?: number;
  allowVideos?: boolean;
  maxFileSizeBytes?: number;
}

const DEFAULT_MAX_FILE_SIZE_BYTES = MAX_UPLOAD_SIZE;
const DEFAULT_MAX_VIDEO_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

async function getAssetFileSize(
  asset: ImagePickerAsset,
): Promise<number | undefined> {
  if (asset.fileSize != null) {
    return asset.fileSize;
  }

  try {
    return (await readLocalFileSize(asset.uri)) ?? undefined;
  } catch (error) {
    console.error("Failed to read media file size:", error);
  }

  return undefined;
}

export async function validateMediaSize(
  asset: ImagePickerAsset,
  maxFileSizeBytes: number,
): Promise<{ isValid: boolean; message: string | null }> {
  const fileSize = await getAssetFileSize(asset);

  if (fileSize == null) {
    return validateFileSize(asset);
  }

  if (fileSize > maxFileSizeBytes) {
    const maxSizeMb = (maxFileSizeBytes / (1024 * 1024)).toFixed(0);

    return {
      isValid: false,
      message: `This file is too large. Maximum size is ${maxSizeMb}MB.`,
    };
  }

  return { isValid: true, message: null };
}

export function MediaCapture({
  media,
  onMediaAdded,
  onMediaRemoved,
  maxPhotos = 10,
  maxVideos = 10,
  allowVideos = true,
  maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
}: MediaCaptureProps) {
  const [loading, setLoading] = useState(false);
  const desktop = useDesktopLayout();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const isDark = colorScheme === "dark";

  const photoCount = media.filter((m) => m.type === "photo").length;
  const videoCount = media.filter((m) => m.type === "video").length;
  const maxFileSizeMb = (maxFileSizeBytes / (1024 * 1024)).toFixed(0);

  const cardBackground = isDark ? "#1a2332" : BrandColors.white;
  const actionButtonBackground = isDark
    ? "rgba(15, 185, 177, 0.08)"
    : BrandColors.primaryLight;
  const emptyIconBackground = isDark
    ? "rgba(15, 185, 177, 0.12)"
    : BrandColors.primaryLight;
  const removeButtonBackground = isDark ? theme.background : BrandColors.white;

  const addValidatedMedia = async (
    asset: ImagePickerAsset,
    type: "photo" | "video",
  ): Promise<boolean> => {
    const normalizedAsset = await normalizeImageAsset(asset);
    const { isValid, message } = await validateMediaSize(
      normalizedAsset,
      type === "photo" ? maxFileSizeBytes : DEFAULT_MAX_VIDEO_SIZE_BYTES,
    );

    if (!isValid) {
      showAlert(
        "File Too Large",
        message ?? `Maximum file size is ${maxFileSizeMb}MB.`,
      );
      return false;
    }

    onMediaAdded({
      uri: normalizedAsset.uri,
      type,
      id: `${Date.now()}-${Math.random()}`,
    });

    return true;
  };

  const handleTakePhoto = async () => {
    try {
      if (photoCount >= maxPhotos) {
        showAlert(
          "Limit Reached",
          `You can only upload up to ${maxPhotos} photos.`,
        );
        return;
      }

      setLoading(true);
      const asset = await pickImageFromCamera(
        { allowsEditing: false },
        true,
        "We need camera access to take photos.",
      );

      if (asset) {
        await addValidatedMedia(asset, "photo");
      }
    } catch (error) {
      console.error("Error taking photo:", error);
      showAlert("Error", "Failed to take photo");
    } finally {
      setLoading(false);
    }
  };

  const handlePickFromGallery = async () => {
    try {
      if (photoCount >= maxPhotos) {
        showAlert(
          "Limit Reached",
          `You can only upload up to ${maxPhotos} photos.`,
        );
        return;
      }

      setLoading(true);
      const assets = await pickImagesFromLibrary(
        {
          allowsEditing: false,
          allowsMultipleSelection: true,
          selectionLimit: maxPhotos - photoCount,
        },
        true,
        "We need photo library access to select images.",
      );

      let addedCount = 0;
      let rejectedCount = 0;

      for (const asset of assets) {
        const added = await addValidatedMedia(asset, "photo");
        if (added) {
          addedCount++;
        } else {
          rejectedCount++;
        }
      }

      if (rejectedCount > 0 && addedCount === 0) {
        showAlert(
          "Files Too Large",
          `${rejectedCount} selected file(s) exceed the ${maxFileSizeMb}MB limit.`,
        );
      }
    } catch (error) {
      console.error("Error picking from gallery:", error);
      showAlert("Error", "Failed to select photos");
    } finally {
      setLoading(false);
    }
  };

  const handleRecordVideo = async () => {
    try {
      if (videoCount >= maxVideos) {
        showAlert(
          "Limit Reached",
          `You can only upload up to ${maxVideos} videos.`,
        );
        return;
      }

      setLoading(true);
      const asset = await recordVideoFromCamera(
        {},
        true,
        "We need camera access to record videos.",
      );

      if (asset) {
        await addValidatedMedia(asset, "video");
      }
    } catch (error) {
      console.error("Error recording video:", error);
      showAlert("Error", "Failed to record video");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMedia = (id: string) => {
    showAlert("Remove Media", "Are you sure you want to remove this item?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Remove",
        style: "destructive",
        onPress: () => onMediaRemoved(id),
      },
    ]);
  };

  const listItem = [
    {
      label: "Take Photos",
      icon: "camera-outline",
      onPress: handleTakePhoto,
      disabled: loading || photoCount >= maxPhotos,
      show: true,
    },
    {
      label: "Gallery",
      icon: "images-outline",
      onPress: handlePickFromGallery,
      disabled: loading || photoCount >= maxPhotos,
      show: true,
    },
    {
      label: "Record Video",
      icon: "videocam-outline",
      onPress: handleRecordVideo,
      disabled: loading || videoCount >= maxVideos,
      show: allowVideos,
    },
  ];

  const mediaGrid =
    media.length > 0 ? (
      <View style={[styles.mediaGrid, desktop && styles.desktopMediaGrid]}>
        {media.map((item) => (
          <View key={item.id} style={styles.mediaItem}>
            <Image
              source={{ uri: item.uri }}
              style={[
                styles.mediaThumbnail,
                desktop && styles.desktopMediaThumbnail,
              ]}
            />
            {item.type === "video" && (
              <View style={styles.videoOverlay}>
                <Ionicons
                  name="play-circle"
                  size={32}
                  color={BrandColors.white}
                />
              </View>
            )}
            <TouchableOpacity
              style={[
                styles.removeButton,
                desktop && styles.desktopRemoveButton,
                { backgroundColor: removeButtonBackground },
              ]}
              onPress={() => handleRemoveMedia(item.id)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons
                name="close"
                size={14}
                color={isDark ? BrandColors.offWhite : BrandColors.charcoal}
              />
            </TouchableOpacity>
          </View>
        ))}
      </View>
    ) : null;

  return (
    <>
      <View
        style={[
          styles.container,
          desktop && styles.desktopContainer,
          {
            backgroundColor: cardBackground,
            borderColor: theme.border,
          },
        ]}
      >
        <View style={styles.header}>
          <ThemedText style={styles.title} type="smallSemiBold">
            Photos & Videos
          </ThemedText>
          <View style={[styles.counters, desktop && styles.desktopCounters]}>
            <ThemedText
              style={[styles.counterText, { color: theme.icon }]}
              type="extraSmall"
            >
              {photoCount}/{maxPhotos} Photos
            </ThemedText>
            {allowVideos && (
              <ThemedText
                style={[styles.counterText, { color: theme.icon }]}
                type="extraSmall"
              >
                {videoCount}/{maxVideos} Videos
              </ThemedText>
            )}
          </View>
        </View>

        <View style={styles.actions}>
          {listItem.map((item) => (
            <TouchableOpacity
              key={item.label}
              style={[
                styles.actionButton,
                desktop && styles.desktopActionButton,
                {
                  backgroundColor: actionButtonBackground,
                  borderColor: BrandColors.primary,
                },
                item.disabled && styles.actionButtonDisabled,
                !item.show && styles.hide,
              ]}
              onPress={item.onPress}
              disabled={item.disabled}
              activeOpacity={0.7}
            >
              <Ionicons
                name={item.icon as any}
                size={28}
                color={BrandColors.primary}
              />
              <ThemedText
                style={[
                  styles.actionButtonText,
                  { color: BrandColors.primary },
                ]}
                type="extraSmallSemiBold"
              >
                {item.label}
              </ThemedText>
            </TouchableOpacity>
          ))}
        </View>

        {loading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={BrandColors.primary} />
            <ThemedText
              style={[styles.loadingText, { color: theme.icon }]}
              type="extraSmall"
            >
              Processing...
            </ThemedText>
          </View>
        )}

        {!desktop && mediaGrid}

        {media.length === 0 && !loading && (
          <View style={styles.emptyState}>
            <View
              style={[
                styles.emptyIconContainer,
                { backgroundColor: emptyIconBackground },
              ]}
            >
              <Ionicons
                name="images-outline"
                size={40}
                color={BrandColors.primary}
              />
            </View>
            <ThemedText style={styles.emptyText} type="smallSemiBold">
              No Photos or Videos yet
            </ThemedText>
            <ThemedText
              style={[styles.emptySubtext, { color: theme.icon }]}
              type="extraSmall"
            >
              Tap the button above to add media
            </ThemedText>
          </View>
        )}
        {desktop && mediaGrid}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: {
    fontSize: 16,
    flex: 1,
  },
  counters: {
    alignItems: "flex-end",
    gap: 2,
  },
  counterText: {
    fontSize: 12,
  },
  actions: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 16,
  },
  actionButton: {
    flex: 1,
    aspectRatio: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderWidth: 1.5,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  actionButtonDisabled: {
    opacity: 0.45,
  },
  actionButtonText: {
    fontSize: 11,
    textAlign: "center",
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 8,
  },
  loadingText: {
    fontSize: 13,
  },
  mediaGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  mediaItem: {
    position: "relative",
  },
  mediaThumbnail: {
    width: 100,
    height: 100,
    borderRadius: 10,
    backgroundColor: BrandColors.lightGray,
  },
  videoOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    borderRadius: 10,
  },
  removeButton: {
    position: "absolute",
    top: -6,
    right: -6,
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  emptyIconContainer: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 15,
    marginBottom: 4,
    textAlign: "center",
  },
  emptySubtext: {
    fontSize: 13,
    textAlign: "center",
  },
  hide: {
    display: "none",
  },
  desktopContainer: {
    marginHorizontal: 0,
    marginBottom: 20,
    borderRadius: 16,
    padding: 20,
  },
  desktopCounters: {
    flexDirection: "row",
    gap: 6,
  },
  desktopActionButton: {
    aspectRatio: undefined,
    minHeight: 80,
  },
  desktopMediaGrid: {
    gap: 16,
  },
  desktopMediaThumbnail: {
    width: 168,
    height: 168,
    borderRadius: 12,
  },
  desktopRemoveButton: {
    top: 8,
    right: 8,
  },
});
